import React, { useState } from "react"
import PropTypes from "prop-types"
import "./clinicVideo.scss"

/**
 * Optional video block from the locations redesign.
 *
 * Sits between Why Choose and the team. A centre with no video renders
 * nothing at all, so the section never appears as an empty frame.
 *
 * Nothing loads from YouTube until someone presses play. The block shows a
 * still with a play button, and only then swaps in the player. That keeps a
 * YouTube embed, roughly a megabyte of script, off every page load, and means
 * visitors who never press play are not handed YouTube cookies. The player
 * itself uses youtube-nocookie.com for the same reason.
 */

/**
 * Pull the eleven-character id out of a YouTube link, whichever shape it is
 * pasted in: a watch URL, a share link, an embed, or the bare id. Anything
 * after the id, a playlist or a timestamp, is dropped, so the video plays and
 * stops rather than rolling into the rest of a playlist.
 */
export const youtubeId = url => {
  if (!url) return ""

  const trimmed = String(url).trim()
  const match = trimmed.match(
    /(?:youtu\.be\/|v=|\/embed\/|\/shorts\/)([A-Za-z0-9_-]{11})/
  )

  if (match) return match[1]

  return /^[A-Za-z0-9_-]{11}$/.test(trimmed) ? trimmed : ""
}

/** YouTube's own still, used when a centre has not supplied a poster image. */
const youtubeStill = videoId =>
  `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`

const embedUrl = videoId =>
  `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0`

const PlayIcon = () => (
  <svg aria-hidden="true" className="ClinicVideo__play" viewBox="0 0 68 48">
    <path
      d="M66.5 7.7a8.6 8.6 0 0 0-6-6.1C55.2 0 34 0 34 0S12.8 0 7.5 1.6a8.6 8.6 0 0 0-6 6.1C0 13 0 24 0 24s0 11 1.5 16.3a8.6 8.6 0 0 0 6 6.1C12.8 48 34 48 34 48s21.2 0 26.5-1.6a8.6 8.6 0 0 0 6-6.1C68 35 68 24 68 24s0-11-1.5-16.3Z"
      fill="#e02f2f"
    />
    <path d="M27 34V14l18 10-18 10Z" fill="#fff" />
  </svg>
)

const ClinicVideo = ({ body, centreName, poster, title, url, videoId }) => {
  const [playing, setPlaying] = useState(false)

  // Contentful's Video URL wins; videoId is the older pinned form.
  const id = youtubeId(url) || videoId
  if (!id) return null

  const heading =
    title || `A look inside our ${centreName} whole child autism center`

  return (
    <section className="ClinicVideo">
      <div className="container-v2">
        {/* The heading is built from the centre's name when none is given,
            so it always has something to show. Body copy is optional. */}
        <div className="ClinicVideo__intro">
          <h2 className="ClinicVideo__title">{heading}</h2>
          {body && <p className="ClinicVideo__body">{body}</p>}
        </div>

        <div className="ClinicVideo__frame">
          {playing ? (
            <iframe
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="ClinicVideo__player"
              frameBorder="0"
              src={embedUrl(id)}
              title={heading}
            />
          ) : (
            <button
              aria-label={`Play video: ${heading}`}
              className="ClinicVideo__poster"
              onClick={() => setPlaying(true)}
              type="button"
            >
              <img
                alt=""
                className="ClinicVideo__still"
                loading="lazy"
                src={poster || youtubeStill(id)}
              />
              <PlayIcon />
            </button>
          )}
        </div>
      </div>
    </section>
  )
}

ClinicVideo.propTypes = {
  body: PropTypes.string,
  centreName: PropTypes.string,
  poster: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string,
  videoId: PropTypes.string,
}

export default ClinicVideo
